import { createSlice } from "@reduxjs/toolkit";
import type { BannerSliceType } from "../../types/banner/banner.model";
import { axiosClient } from "../../configs/axiosClient";
import { API } from "../../configs/endpoints";
import { addAsyncCases, createApiThunk } from "../createApiThunk";
import type {
  CreateBannerResponse,
  GetBannerResponse,
  GetBannersResponse,
  UpdateBannerResponse,
} from "../../types/banner/banner.response";
import type {
  CreateBannerPayload,
  GetBannerPayload,
  UpdateBannerPayload,
} from "../../types/banner/banner.payload";

const initialState: BannerSliceType = {
  loading: {
    list: false,
    detail: false,
    create: false,
    update: false,
    delete: false,
  },
  error: null,
  banner: null,
  banners: [],
  pagination: null,
};

export const createBanner = createApiThunk<
  CreateBannerResponse,
  CreateBannerPayload
>(
  "banner/createBanner",
  async (data) => (await axiosClient.post(API.banner.create, data)).data,
  "Tạo banner thất bại",
);

export const getBanners = createApiThunk<
  GetBannersResponse,
  GetBannerPayload
>(
  "banner/getBanners",
  async (query) =>
    (await axiosClient.get(API.banner.list, { params: query })).data,
  "Lấy danh sách banner thất bại",
);

export const deleteBanner = createApiThunk<string, string>(
  "banner/deleteBanner",
  async (id) => (await axiosClient.delete(API.banner.delete(id))).data,
  "Xóa banner thất bại",
);

export const getBanner = createApiThunk<GetBannerResponse, string>(
  "banner/getBanner",
  async (id) => (await axiosClient.get(API.banner.detail(id))).data,
  "Lấy thông tin banner thất bại",
);

export const updateBanner = createApiThunk<
  UpdateBannerResponse,
  UpdateBannerPayload
>(
  "banner/updateBanner",
  async ({ id, data }) =>
    (await axiosClient.put(API.banner.update(id), data)).data,
  "Sửa banner thất bại",
);

const bannerSlice = createSlice({
  name: "banner",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    addAsyncCases({
      builder,
      thunk: createBanner,
      loadingKey: "create",
      fallbackError: "Tạo banner thất bại",
      onSuccess: (state, payload) => {
        state.banners.push(payload.banner);
      },
    });
    addAsyncCases({
      builder,
      thunk: getBanners,
      loadingKey: "list",
      fallbackError: "Lấy danh sách banner thất bại",
      onSuccess: (state, payload) => {
        state.banners = payload.banners;
        state.pagination = payload.pagination;
      },
    });
    addAsyncCases({
      builder,
      thunk: deleteBanner,
      loadingKey: "delete",
      fallbackError: "Xóa banner thất bại",
      onSuccess: (state, payload) => {
        state.banners = state.banners.filter((b) => b._id !== payload);
      },
    });
    addAsyncCases({
      builder,
      thunk: getBanner,
      loadingKey: "detail",
      fallbackError: "Lấy thông tin banner thất bại",
      onSuccess: (state, payload) => {
        state.banner = payload.banner;
      },
    });
    addAsyncCases({
      builder,
      thunk: updateBanner,
      loadingKey: "update",
      fallbackError: "Sửa banner thất bại",
      onSuccess: (state, payload) => {
        const updated = payload.banner;
        const idx = state.banners.findIndex((b) => b._id === updated._id);
        if (idx !== -1) state.banners[idx] = updated;
      },
    });
  },
});

export default bannerSlice.reducer;
